import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Users } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const Grupo = ({ g, onDone, isAdmin }) => {
  const [keep, setKeep] = useState(g[0].id);
  const [busy, setBusy] = useState(false);
  const unir = async () => {
    const otros = g.filter((p) => p.id !== keep).map((p) => p.id);
    if (!window.confirm(`¿Unir ${g.length} fichas en «${g.find((p) => p.id === keep).nombre}»? Se completan sus datos vacíos con los de las otras, se pasan a ella todos los tratamientos, presupuestos, citas y cuestionarios, y se borran las otras fichas.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/patients/unir", { principal_id: keep, otros });
      toast.success(`Unidas. ${data.rellenados.length ? `Datos completados: ${data.rellenados.length}` : "No faltaba ningún dato"}`);
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  const noSon = async () => {
    await api.post("/patients/no-duplicados", { ids: g.map((p) => p.id) });
    toast.success("Marcados como personas distintas");
    onDone();
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`dup-grupo-${g[0].id}`}>
      <div className={`mb-2 text-[11px] uppercase tracking-wider ${g.some((p) => p.motivo.includes("fecha")) ? "text-rose-600 font-semibold" : "text-slate-500"}`}>{g.some((p) => p.motivo.includes("fecha")) ? "Nombre parecido pero fecha de nacimiento distinta: revisa antes de unir" : g[0].motivo}</div>
      <div className="divide-y divide-slate-100">
        {g.map((p) => (
          <label key={p.id} className="flex flex-wrap items-center gap-3 py-2 text-sm cursor-pointer" data-testid={`dup-p-${p.id}`}>
            <input type="radio" name={`keep-${g[0].id}`} checked={keep === p.id} onChange={() => setKeep(p.id)} className="h-4 w-4 accent-[#1E3A8A]" data-testid={`dup-keep-${p.id}`} />
            <Link to={`/pacientes/${p.id}`} target="_blank" className="min-w-[220px] flex-1 font-medium text-[#1E3A8A] hover:underline">{p.nombre}</Link>
            <span className="text-xs text-slate-500 w-24">{p.codigo || "sin código"}</span>
            <span className="text-xs text-slate-500 w-24">{p.centro || "—"}</span>
            <span className="text-xs text-slate-500 w-28">{p.telefono || "sin teléfono"}</span>
            <span className="text-xs text-slate-500 w-24">{p.fecha_nacimiento ? fmtDate(p.fecha_nacimiento) : "sin f. nac."}</span>
            <span className={`text-xs w-28 ${p.tratamientos ? "font-semibold text-emerald-700" : "text-slate-400"}`}>{p.tratamientos} tratamiento{p.tratamientos === 1 ? "" : "s"}</span>
            {keep === p.id && <span className="rounded bg-[#1E3A8A] px-2 py-0.5 text-[10px] text-white">Se queda esta</span>}
          </label>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        {isAdmin && <button onClick={unir} disabled={busy} data-testid={`dup-unir-${g[0].id}`} className="rounded-md bg-[#1E3A8A] px-3 py-1.5 text-xs text-white disabled:opacity-60">Unir en la marcada</button>}
        <button onClick={noSon} data-testid={`dup-no-${g[0].id}`} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs text-slate-700">No son la misma persona</button>
      </div>
    </div>
  );
};

export default function Duplicados() {
  const [grupos, setGrupos] = useState(null);
  const isAdmin = useAuth().user?.role === "admin";
  const load = () => api.get("/patients/duplicados-grupos").then((r) => setGrupos(r.data));
  useEffect(() => {
    load();
  }, []);
  return (
    <div className="space-y-4" data-testid="duplicados-page">
      <Link to="/pacientes" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft className="h-4 w-4" />Control de pacientes</Link>
      <h1 className="font-[Outfit] text-3xl font-bold flex items-center gap-2"><Users className="h-7 w-7" />Posibles duplicados</h1>
      <p className="text-sm text-slate-600 max-w-3xl">Se comparan los nombres sin importar el orden (nombre y apellidos o apellidos y nombre), ni acentos ni mayúsculas, y se aceptan pequeñas erratas (una letra de más, de menos o cambiada). Si la fecha de nacimiento es distinta se muestran en rojo para que lo revises. Marca la ficha que se queda (por defecto, la que tiene tratamientos) y pulsa «Unir».</p>
      {grupos === null && <div className="text-slate-400">Buscando…</div>}
      {grupos?.length === 0 && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="dup-vacio">No hay duplicados.</div>}
      {grupos?.length > 0 && <div className="text-sm font-semibold" data-testid="dup-total">{grupos.length} grupos</div>}
      {grupos?.map((g) => <Grupo key={g.map((p) => p.id).join()} g={g} isAdmin={isAdmin} onDone={load} />)}
    </div>
  );
}
